import React, { useEffect, useState, useCallback } from "react";
import { SignalPulse } from "../components/SignalPulse";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Lock, Pencil, Trash2, Share2, ArrowUp, ArrowDown, Star, Type, Check, X, Layers } from "lucide-react";
import api from "../lib/api";
import { streamThumb } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCrypto } from "../context/CryptoContext";
import { PostCard } from "./Feed";
import { SecureMedia, Avatar } from "../components/Media";
import PostDesigner from "../components/PostDesigner";

export default function Collection() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { decryptNetText, encryptNetText, encryptNetBytes, hasNetwork } = useCrypto();
  const [col, setCol] = useState(null); // null loading | false not found
  const [title, setTitle] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(null);
  const [designing, setDesigning] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const load = useCallback(() => {
    api.get(`/collections/${id}`).then(({ data }) => setCol(data)).catch(() => setCol(false));
  }, [id]);
  useEffect(() => { setCol(null); load(); }, [id, load]);

  useEffect(() => {
    (async () => {
      if (!col) return;
      if (col.enc && col.title_ct) { const t = await decryptNetText(col.title_ct, col.title_iv); setTitle(t === null ? "__LOCKED__" : t); }
      else setTitle(col.title || "Untitled collection");
    })();
  }, [col, decryptNetText]);

  if (col === null) return <div className="flex justify-center py-32"><SignalPulse size={44} /></div>;
  if (col === false) return <div className="text-center py-32 font-body text-zinc-500" data-testid="collection-not-found">Collection not found.</div>;

  const isOwner = user?.id === col.owner_id;
  const author = col.posts?.[0]?.author;

  const saveTitle = async () => {
    setBusy("title");
    try {
      let payload = { title: draft, enc: false };
      if (hasNetwork()) { const e = await encryptNetText(draft); payload = { title_ct: e.ct, title_iv: e.iv, enc: true, title: "" }; }
      await api.patch(`/collections/${id}`, payload);
      setEditing(false); load();
    } catch { alert("Could not rename the collection."); }
    setBusy(null);
  };

  const onDesigned = async (file) => {
    setDesigning(false); setBusy("cover");
    try {
      const fd = new FormData();
      let cover;
      if (hasNetwork()) {
        const buf = await file.arrayBuffer();
        const enc = await encryptNetBytes(buf);
        fd.append("file", new Blob([enc.ctBuf]), "cover.bin"); fd.append("enc", "true");
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        cover = { path: data.path, type: "image", iv: enc.ivB64 };
        await api.patch(`/collections/${id}`, { cover, cover_enc: true });
      } else {
        fd.append("file", file, "cover.jpg"); fd.append("enc", "false");
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        cover = { path: data.path, type: "image", iv: null };
        await api.patch(`/collections/${id}`, { cover, cover_enc: false });
      }
      load();
    } catch { alert("Could not set the title card."); }
    setBusy(null);
  };

  const move = async (idx, dir) => {
    const ids = col.posts.map((p) => p.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    setBusy("order");
    try { await api.patch(`/collections/${id}`, { post_ids: ids }); load(); } catch { alert("Could not reorder."); }
    setBusy(null);
  };

  const setCover = async (postId) => { setBusy("cover"); try { await api.patch(`/collections/${id}`, { cover_post_id: postId }); load(); } catch {} setBusy(null); };
  const removePost = async (postId) => { setBusy("rm"); try { await api.delete(`/collections/${id}/posts/${postId}`); load(); } catch {} setBusy(null); };
  const del = async () => { try { await api.delete(`/collections/${id}`); navigate(`/profile/${col.owner_id}`); } catch { alert("Could not delete."); } };
  const handlePostDelete = async (postId) => { try { await api.delete(`/posts/${postId}`); load(); } catch { alert("Could not delete the post."); } };
  const share = () => { try { navigator.clipboard.writeText(`${window.location.origin}/collection/${id}`); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {} };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8" data-testid="collection-page">
      {designing && <PostDesigner mode="text" onDone={onDesigned} onCancel={() => setDesigning(false)} />}
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-zinc-500 hover:text-white text-xs mb-8 font-body"><ArrowLeft size={14} /> Back</button>

      <div className="relative border border-white/10 overflow-hidden mb-8" data-testid="collection-hero">
        <div className="aspect-[16/7] bg-gradient-to-br from-[#14A89A]/25 via-[#0A0A0A] to-[#0A0A0A]">
          {col.cover && col.cover.stream_thumb ? (
            <img src={streamThumb(col.cover.stream_thumb)} alt="" className="w-full h-full object-cover" />
          ) : col.cover && col.cover.media && col.cover.media.path ? (
            <SecureMedia path={col.cover.media.path} iv={col.cover.media.iv} enc={col.cover.enc} type={col.cover.media.type} className="w-full h-full object-cover" />
          ) : null}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="flex items-center gap-1.5 text-[#14A89A] mb-1"><Layers size={13} /><span className="font-body text-[10px] tracking-[0.2em] uppercase">Collection · {col.post_count} {col.post_count === 1 ? "piece" : "pieces"}</span></div>
          {editing ? (
            <div className="flex items-center gap-2">
              <input value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="collection-title-input" className="flex-1 bg-black/50 border border-white/20 px-3 py-2 text-white font-heading text-xl focus:outline-none focus:border-[#14A89A]" />
              <button onClick={saveTitle} disabled={busy === "title"} data-testid="collection-title-save" className="bg-[#14A89A] text-[#0A0A0A] p-2">{busy === "title" ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}</button>
              <button onClick={() => setEditing(false)} className="text-zinc-300 p-2"><X size={16} /></button>
            </div>
          ) : (
            <h1 className="font-heading text-2xl md:text-3xl text-white flex items-center gap-2" data-testid="collection-title">
              {title === "__LOCKED__" ? <span className="text-zinc-400 flex items-center gap-2 text-lg"><Lock size={16} /> Encrypted</span> : title}
              {isOwner && title !== "__LOCKED__" && <button onClick={() => { setDraft(title); setEditing(true); }} data-testid="collection-rename-button" className="text-zinc-400 hover:text-white"><Pencil size={16} /></button>}
            </h1>
          )}
          {author && <button onClick={() => navigate(`/profile/${col.owner_id}`)} className="mt-2 flex items-center gap-2 text-zinc-300 hover:text-white"><Avatar name={author.name} src={author.avatar} size={26} /><span className="font-body text-sm">{author.name}</span></button>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button onClick={share} data-testid="collection-share-button" className="flex items-center gap-2 border border-white/15 text-zinc-300 hover:border-[#14A89A] hover:text-white px-4 py-2 font-body text-sm transition-colors"><Share2 size={15} /> {copied ? "Link copied" : "Share"}</button>
        {isOwner && <button onClick={() => setDesigning(true)} data-testid="collection-design-cover-button" className="flex items-center gap-2 border border-white/15 text-zinc-300 hover:border-[#D4AF37] hover:text-white px-4 py-2 font-body text-sm transition-colors"><Type size={15} /> Design title card</button>}
        {isOwner && !confirmDel && <button onClick={() => setConfirmDel(true)} data-testid="collection-delete-button" className="flex items-center gap-2 text-zinc-600 hover:text-red-500 px-2 py-2 font-body text-sm transition-colors ml-auto"><Trash2 size={15} /> Delete</button>}
        {isOwner && confirmDel && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="font-body text-xs text-zinc-400">Delete this collection? (Your posts stay.)</span>
            <button onClick={del} data-testid="collection-delete-confirm" className="bg-red-600 text-white px-3 py-1.5 font-body text-xs hover:bg-red-500 transition-colors">Delete</button>
            <button onClick={() => setConfirmDel(false)} className="text-zinc-400 px-2 font-body text-xs">Cancel</button>
          </div>
        )}
      </div>
      {isOwner && <p className="font-body text-xs text-zinc-500 mb-8" data-testid="collection-owner-hint">Ordered oldest → newest by default. Use the arrows on each piece to set your own order, star any piece to use as the cover, or design a title card above.</p>}

      {(!col.posts || col.posts.length === 0) ? (
        <p className="font-body text-sm text-zinc-600 py-12" data-testid="collection-empty">Nothing in this collection yet.{isOwner ? " Add posts from your profile (the layers icon on a post) or while transmitting." : ""}</p>
      ) : (
        <div className="flex flex-col gap-12">
          {col.posts.map((p, i) => (
            <div key={p.id} data-testid={`collection-item-${i}`}>
              {isOwner && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="font-body text-[10px] tracking-[0.2em] uppercase text-zinc-500">#{i + 1}</span>
                  <button onClick={() => move(i, -1)} disabled={i === 0 || !!busy} data-testid={`collection-up-${i}`} title="Move up" className="text-zinc-500 hover:text-white disabled:opacity-30"><ArrowUp size={15} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === col.posts.length - 1 || !!busy} data-testid={`collection-down-${i}`} title="Move down" className="text-zinc-500 hover:text-white disabled:opacity-30"><ArrowDown size={15} /></button>
                  <button onClick={() => setCover(p.id)} data-testid={`collection-setcover-${i}`} title="Use as cover" className={`flex items-center gap-1 ${col.cover_post_id === p.id ? "text-[#D4AF37]" : "text-zinc-500 hover:text-[#D4AF37]"}`}><Star size={14} className={col.cover_post_id === p.id ? "fill-[#D4AF37]" : ""} /><span className="font-body text-[10px]">{col.cover_post_id === p.id ? "Cover" : "Set cover"}</span></button>
                  <button onClick={() => removePost(p.id)} data-testid={`collection-remove-${i}`} title="Remove from collection" className="text-zinc-500 hover:text-red-500 ml-auto flex items-center gap-1"><X size={14} /><span className="font-body text-[10px]">Remove</span></button>
                </div>
              )}
              <PostCard post={p} onNav={navigate} onDelete={handlePostDelete} currentUser={user} onChanged={load} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
